import { useAtom } from "jotai";
import { showModal } from "@store/categoryStore";
import { translate } from "@helpers/translationObjectFormatter";
import {
    ICompareCategoryProps,
    TranslationsObject,
} from "@genericTypes/sharedTypes";
import useWindowSize from "@hooks/useWindowSize";
import styles from "./style.module.scss";

import PlusIcon from "./icons/plusIcon";
import TicIcon from "@components/shared/bestBuyers/icons/TicIcon";
import XIcon from "@components/shared/bestBuyers/icons/XIcon";
import {
    getDirection,
    PLACE_HOLDER_IMAGE_PATH,
    titleDecoder,
} from "@lib/sharedUtils";

import Image from "next/legacy/image";
import XIconMain from "./icons/XIcon";
import {
    getAllGroupsFromAttributes,
    getAttributesComparisons,
    featureListOfferValueHandler,
} from "@lib/sharedUtils";
import { useQuery } from "@tanstack/react-query";
import OfferLinks from "@components/shared/offerLinks";
import { useEffect, useMemo, useRef } from "react";
import { GQLOffers, GQLCategories } from "@gql/types/gql";
import { Session } from "pages/api/session";
import Tooltip from "@components/tooltip";

export interface OfferWithHeight extends GQLOffers {
    height?: number;
}

export default function Comparisons({
    comparisonList,
    upDateList,
}: {
    comparisonList: GQLOffers[];
    upDateList: CallableFunction;
}) {
    // const { query } = useRouter();
    // const id = query.id as string;

    const { data: props } = useQuery<ICompareCategoryProps, Error>([
        "compareCategoryProps",
    ]);
    const { category, textBlocks } = props ?? {};
    const { data: session } = useQuery<Session, Error>({
        queryKey: ["session"],
    });
    const { data: translations } = useQuery<TranslationsObject>([
        "translations",
    ]);

    const allGroups = getAllGroupsFromAttributes(
        category as GQLCategories,
    ).filter((group) => group?.slug !== "quick-stats");

    const heightEqualizerArrayToEqual = [
        "divHeightTitle",
        "divHeightPros",
        "divHeightCons",
        "divHeightSellingPoints",
        ...allGroups.map((group) => `divHeight${group.name}`),
        "divHeightQuickOverview",
        "divHeightQuickStats",
    ];

    const [, setModalVisibility] = useAtom(showModal);
    const windowSize = useWindowSize();
    const showPlusIcon =
        windowSize.width < 1024
            ? comparisonList?.length === 0
                ? true
                : false
            : comparisonList?.length < 3;

    const showModalHandler = () => {
        setModalVisibility(true);
    };
    useEffect(() => {
        const heightEqualizer = () => {
            const newArr = comparisonList?.map((buyer, index) => {
                const allHeights = heightEqualizerArrayToEqual.map(
                    (classToEqual) => {
                        return document.getElementsByClassName(classToEqual)[
                            index
                        ]
                            ? document
                                  .getElementsByClassName(classToEqual)
                                  [index].getBoundingClientRect().height
                            : 0;
                    },
                );
                const buyerHeight = Math.max(...allHeights);

                return { ...buyer, height: buyerHeight };
            });

            upDateList(newArr);
        };
        let debounceSet: string | number | NodeJS.Timeout | undefined;
        if (windowSize.width < 1024) {
            debounceSet = setTimeout(() => {
                heightEqualizer();
            }, 300);
        }
        return () => {
            clearTimeout(debounceSet);
        };
    }, [comparisonList?.length, windowSize.width]);
    const shadowShow =
        windowSize.width < 1024
            ? true
            : comparisonList.length > 3
            ? true
            : false;
    const textBlocksExists =
        textBlocks &&
        textBlocks.allTextBlocks &&
        textBlocks.allTextBlocks.length > 0;
    const section = useRef<HTMLDivElement>(null);
    const fullyScrolled = useMemo(() => {
        if (section.current) {
            return (
                section.current.scrollWidth - section.current.clientWidth ===
                section.current.scrollLeft
            );
        }
        return false;
    }, [
        section.current?.scrollLeft,
        section.current?.scrollWidth,
        section.current?.clientWidth,
    ]);
    return (
        <section ref={section} className={`flex lg:mt-12 mt-8 relative`}>
            <div
                className={` flex lg:flex-col lg:max-w-[1280px] overflow-x-auto  relative`}
            >
                <div
                    className={`flex   flex-col lg:flex-row ${
                        styles["stickyDiv"]
                    } ${getDirection() === "ltr" ? "left:0" : ""}`}
                >
                    {(comparisonList as OfferWithHeight[])?.map((vendor) => (
                        <div
                            key={vendor.id}
                            className={`border-r lg:border lg:w-[360px] lg:min-w-[360px]  divHeightTitle `}
                            style={{
                                height: vendor?.height
                                    ? windowSize.width < 1024
                                        ? vendor.height
                                        : "auto"
                                    : "auto",
                            }}
                        >
                            <div
                                className={`${styles["backgroundColor"]} w-full h-8`}
                            ></div>
                            <div
                                className={` pt-4 pb-6 text-center w-[140px] px-3 lg:px-0 lg:w-auto flex flex-col justify-center lg:block `}
                            >
                                <div className="flex justify-end lg:me-5 relative  lg:bottom-0 ">
                                    <span
                                        className={`cursor-pointer ${styles["x-icon"]}`}
                                        onClick={() => {
                                            const arr = comparisonList.filter(
                                                (offer) =>
                                                    offer.id !== vendor.id,
                                            );
                                            upDateList(arr);
                                        }}
                                    >
                                        <XIconMain />
                                    </span>
                                </div>
                                {vendor?.logo?.fullPath ? (
                                    <span className="mt-5">
                                        <Image
                                            src={vendor?.logo?.fullPath}
                                            alt="logo"
                                            // layout="fill"
                                            objectFit="contain"
                                            width={130}
                                            height={50}
                                        />
                                    </span>
                                ) : (
                                    <>
                                        <Image
                                            src={PLACE_HOLDER_IMAGE_PATH}
                                            alt="logo"
                                            objectFit="contain"
                                            width={130}
                                            height={50}
                                        />
                                    </>
                                )}
                                <div className=" lg:w-[70%] w-full mx-auto mt-5">
                                    <OfferLinks
                                        vendor={vendor}
                                        categoryId={category?.id as number}
                                        classNameButton={
                                            styles["linkButtonStyle"]
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className=" flex flex-col lg:flex-row">
                    {(comparisonList as OfferWithHeight[]).map((vendor) => (
                        <div
                            className={`w-[295px] lg:w-[360px] lg:min-w-[360px] lg:border divHeightPros`}
                            key={vendor.id}
                            style={{
                                height: vendor.height
                                    ? windowSize.width < 1024
                                        ? vendor.height
                                        : "auto"
                                    : "auto",
                            }}
                        >
                            <div
                                className={`${styles["titleColors"]} lg:py-4  font-extrabold lg:text-lg lg:leading-5 text-sm leading-4 py-2 ps-4 border-r lg:border-r-0`}
                            >
                                {translate(
                                    translations,
                                    "several_general_prosWord",
                                )}
                            </div>

                            <ul
                                className={`${styles["devColors"]} px-4 pt-8 pb-8 text-sm  border lg:border-0 ${styles["heightStyle"]}`}
                            >
                                {vendor.pros
                                    ?.map((pros) => pros?.description)
                                    ?.map((pros, index) => (
                                        <li
                                            key={index}
                                            className=" flex items-baseline gap-2 pb-2"
                                        >
                                            <span
                                                className={`${styles["ticIconStyle"]}  `}
                                            >
                                                <TicIcon color="#1693F0" />
                                            </span>

                                            <span className=" block mt-1">
                                                {pros}
                                            </span>
                                        </li>
                                    ))}
                            </ul>
                        </div>
                    ))}
                </div>
                <div className=" flex flex-col lg:flex-row">
                    {(comparisonList as OfferWithHeight[]).map((vendor) => (
                        <div
                            className={`w-[295px] lg:w-[360px] lg:min-w-[360px] lg:border divHeightCons `}
                            key={vendor.id}
                            style={{
                                height: vendor.height
                                    ? windowSize.width < 1024
                                        ? vendor.height
                                        : "auto"
                                    : "auto",
                            }}
                        >
                            <div
                                className={`${styles["titleColors"]} lg:py-4  font-extrabold lg:text-lg lg:leading-5 text-sm leading-4 py-2 ps-4 border-r lg:border-r-0`}
                            >
                                {translate(
                                    translations,
                                    "several_general_consWord",
                                )}
                            </div>

                            <ul
                                className={`${styles["devColors"]} px-4 pt-8 pb-8 text-sm  border lg:border-0 ${styles["heightStyle"]}`}
                            >
                                {vendor.cons
                                    ?.map((cons) => cons?.description)
                                    ?.map((cons, index) => (
                                        <li
                                            key={index}
                                            className="flex items-baseline gap-2 pb-2"
                                        >
                                            <span
                                                className={`${styles["xIconStyle"]}  `}
                                            >
                                                <XIcon color="#E85951" />
                                            </span>

                                            <span className=" block mt-1">
                                                {cons}
                                            </span>
                                        </li>
                                    ))}
                            </ul>
                        </div>
                    ))}
                </div>
                {!textBlocksExists &&
                    comparisonList.every((item) => item.quickOverview) && (
                        <div className=" flex flex-col lg:flex-row">
                            {(comparisonList as OfferWithHeight[]).map(
                                (vendor) => (
                                    <div
                                        className={` lg:border w-[295px] lg:w-[360px] lg:min-w-[360px] divHeightQuickOverview `}
                                        key={vendor.id}
                                        style={{
                                            height: vendor.height
                                                ? windowSize.width < 1024
                                                    ? vendor.height
                                                    : "auto"
                                                : "auto",
                                        }}
                                    >
                                        {vendor.quickOverview && (
                                            <>
                                                <div
                                                    className={`${styles["titleColors"]} lg:py-4  font-extrabold lg:text-lg lg:leading-5 text-sm leading-4 py-2 ps-4 border-r lg:border-r-0`}
                                                >
                                                    {translate(
                                                        translations,
                                                        "several_comparisons-quickOverviewTitle",
                                                    )}
                                                </div>
                                                <div
                                                    className={`${styles["devColors"]} px-4 pt-8 pb-8 text-sm  border lg:border-0 ${styles["heightStyle"]}`}
                                                    dangerouslySetInnerHTML={{
                                                        __html: titleDecoder(
                                                            vendor.quickOverview ??
                                                                "",
                                                            {
                                                                category:
                                                                    category?.name as string,
                                                                region: session
                                                                    ?.visitDetails
                                                                    ?.region,
                                                            },
                                                        ),
                                                    }}
                                                />
                                            </>
                                        )}
                                    </div>
                                ),
                            )}
                        </div>
                    )}
                {comparisonList.every((item) =>
                    item.attributes?.some(
                        (attr) =>
                            attr?.baseAttribute?.groupSlug === "quick-stats",
                    ),
                ) && (
                    <div className=" flex flex-col lg:flex-row">
                        {(comparisonList as OfferWithHeight[]).map((vendor) => (
                            <div
                                className={` lg:border w-[295px] lg:w-[360px] lg:min-w-[360px] divHeightQuickStats `}
                                key={vendor.id}
                                style={{
                                    height: vendor.height
                                        ? windowSize.width < 1024
                                            ? vendor.height
                                            : "auto"
                                        : "auto",
                                }}
                            >
                                {(vendor.attributes?.filter(
                                    (attr) =>
                                        attr?.baseAttribute?.groupSlug ===
                                        "quick-stats",
                                ).length as number) > 0 && (
                                    <>
                                        <div
                                            className={`${styles["titleColors"]} lg:py-4  font-extrabold lg:text-lg lg:leading-5 text-sm leading-4 py-2 ps-4 border-r lg:border-r-0`}
                                        >
                                            {translate(
                                                translations,
                                                "several_comparisons-quickStatsTitle",
                                            )}
                                        </div>
                                        <ul
                                            className={`${styles["devColors"]} px-4 pt-8 pb-8 text-sm  border lg:border-0 ${styles["heightStyle"]}`}
                                        >
                                            {vendor.attributes
                                                ?.filter(
                                                    (attr) =>
                                                        attr?.baseAttribute
                                                            ?.groupSlug ===
                                                        "quick-stats",
                                                )
                                                .map((attr, index) => (
                                                    <li
                                                        key={index}
                                                        className="flex items-baseline gap-2 pb-2"
                                                    >
                                                        <div className=" block mt-1">
                                                            <span className="font-bold">
                                                                {
                                                                    attr
                                                                        ?.baseAttribute
                                                                        ?.name
                                                                }{" "}
                                                                :
                                                            </span>{" "}
                                                            <span>
                                                                {attr?.value}
                                                            </span>
                                                        </div>
                                                    </li>
                                                ))}
                                        </ul>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                )}
                <div className=" flex flex-col lg:flex-row">
                    {(comparisonList as OfferWithHeight[]).map((vendor) => (
                        <div
                            className={` lg:border w-[295px] lg:w-[360px] lg:min-w-[360px] divHeightSellingPoints `}
                            key={vendor.id}
                            style={{
                                height: vendor.height
                                    ? windowSize.width < 1024
                                        ? vendor.height
                                        : "auto"
                                    : "auto",
                            }}
                        >
                            {(vendor?.features?.length as number) > 0 && (
                                <>
                                    <div
                                        className={`${styles["titleColors"]} lg:py-4  font-extrabold lg:text-lg lg:leading-5 text-sm leading-4 py-2 ps-4 border-r lg:border-r-0`}
                                    >
                                        {translate(
                                            translations,
                                            "several_general_sellingPointsWord",
                                        )}
                                    </div>
                                    <ul
                                        className={`${styles["devColors"]} px-4 pt-8 pb-8 text-sm  border lg:border-0 ${styles["heightStyle"]}`}
                                    >
                                        {vendor.features?.map(
                                            (feature, index) => (
                                                <li
                                                    key={index}
                                                    className="flex items-baseline gap-2 pb-2"
                                                >
                                                    <span
                                                        className={`${styles["ticIconStyle"]}  `}
                                                    >
                                                        <TicIcon color="#1693F0" />
                                                    </span>

                                                    <span className=" block mt-1">
                                                        {feature?.description}
                                                    </span>
                                                </li>
                                            ),
                                        )}
                                    </ul>
                                </>
                            )}
                        </div>
                    ))}
                </div>
                {allGroups.map((group, index) => (
                    <div className=" flex flex-col lg:flex-row" key={index}>
                        {(comparisonList as OfferWithHeight[]).map((vendor) => (
                            <div
                                className={`border-r lg:border w-[295px] lg:w-[360px] lg:min-w-[360px] divHeight${group.name} `}
                                key={vendor.id}
                                style={{
                                    height: vendor.height
                                        ? windowSize.width < 1024
                                            ? vendor.height
                                            : "auto"
                                        : "auto",
                                }}
                            >
                                <div
                                    className={`${styles["titleColors"]} lg:py-4  font-extrabold lg:text-lg lg:leading-5 text-sm leading-4 py-2 ps-4 border-r lg:border-r-0`}
                                >
                                    {group.name}
                                </div>

                                <ul
                                    className={`${styles["devColors"]} px-4 pt-8 pb-8 text-sm  border lg:border-0 ${styles["heightStyle"]}`}
                                >
                                    {getAttributesComparisons(
                                        category as GQLCategories,
                                        vendor,
                                        group.id,
                                    )
                                        ?.filter(
                                            (attr) => attr.value !== "text",
                                        )
                                        ?.map((attr, index) => (
                                            <li
                                                key={index}
                                                className="flex items-baseline gap-2 pb-2"
                                            >
                                                {/* <div>{attr.value}</div> */}
                                                {featureListOfferValueHandler(
                                                    attr.value,
                                                ) === "Available" ? (
                                                    <span
                                                        className={`${styles["ticIconStyle"]}  `}
                                                    >
                                                        <TicIcon color="#1693F0" />
                                                    </span>
                                                ) : featureListOfferValueHandler(
                                                      attr.value,
                                                  ) === "Not Available" ? (
                                                    <span
                                                        className={`${styles["xIconStyle"]}  `}
                                                    >
                                                        <XIcon color="#E85951" />
                                                    </span>
                                                ) : (
                                                    <Tooltip
                                                        tooltipMessageKey="several_category_compare_page_tooltip"
                                                        className={
                                                            styles.tooltip
                                                        }
                                                    >
                                                        <div
                                                            style={{
                                                                fontSize:
                                                                    "14px",
                                                                fontWeight:
                                                                    "700",
                                                                color: "#f9bb20",
                                                            }}
                                                        >
                                                            &#8212;
                                                        </div>
                                                    </Tooltip>
                                                )}
                                                <span className=" block mt-1">
                                                    {attr.label}
                                                </span>
                                            </li>
                                        ))}
                                    {getAttributesComparisons(
                                        category as GQLCategories,
                                        vendor,
                                        group.id,
                                    )
                                        ?.filter(
                                            (attr) => attr.value === "text",
                                        )
                                        ?.map((attr, index) => (
                                            <li
                                                key={index}
                                                className={`flex items-baseline gap-2 pb-2 ${
                                                    index === 0
                                                        ? "border-t border-t-[#eee] pt-4 mt-4"
                                                        : ""
                                                }`}
                                            >
                                                <span className=" block mt-1">
                                                    {attr.label}
                                                </span>
                                            </li>
                                        ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
            {shadowShow && fullyScrolled && (
                <div className={`${styles["boxShadowStyle"]} `}></div>
            )}

            {showPlusIcon && (
                <div
                    className={`${
                        styles["borderColor"]
                    } border lg:w-[366px] lg:h-[219px] w-[290px] h-[200px] flex flex-col justify-center items-center gap-5 ${
                        comparisonList.length > 0
                            ? ""
                            : "mx-auto relative right-[5px] lg:right-0"
                    }`}
                >
                    <span
                        className={`cursor-pointer ${styles["add-icon"]}`}
                        onClick={showModalHandler}
                    >
                        <PlusIcon />
                    </span>

                    <h3 className=" text-lg leading-7">
                        {translate(
                            translations,
                            "several_comparePage_addCompare",
                        )}
                    </h3>
                </div>
            )}
        </section>
    );
}
